import { AfterViewInit, Directive, ElementRef, HostListener, NgZone, OnDestroy, Renderer2 } from '@angular/core';

const CHEVRON_LEFT_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12.3335 15.5L5.18683 8.35333C5.14039 8.30696 5.10354 8.25188 5.0784 8.19125C5.05327 8.13062 5.04033 8.06563 5.04033 8C5.04033 7.93437 5.05327 7.86938 5.0784 7.80875C5.10354 7.74812 5.14039 7.69304 5.18683 7.64667L12.3335 0.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const CHEVRON_RIGHT_SVG = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3.6665 0.5L10.8132 7.64667C10.8596 7.69304 10.8965 7.74812 10.9216 7.80875C10.9467 7.86938 10.9597 7.93437 10.9597 8C10.9597 8.06563 10.9467 8.13062 10.9216 8.19125C10.8965 8.25188 10.8596 8.30696 10.8132 8.35333L3.6665 15.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// Sub-pixel scroll positions never land exactly on the min/max, so edge checks need a little slack.
const EDGE_TOLERANCE = 1;

@Directive({
  selector: '[appDragScroll]',
  standalone: false,
})
export class DragScrollDirective implements AfterViewInit, OnDestroy {
  private isDown = false;
  private startX = 0;
  private scrollLeft = 0;
  private prevArrow: HTMLElement;
  private nextArrow: HTMLElement;
  private unlisteners: (() => void)[] = [];
  resizeObserver: ResizeObserver;

  constructor(private el: ElementRef, private renderer: Renderer2, private zone: NgZone) {}

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.unlisteners.forEach((unlisten) => unlisten());
    this.prevArrow?.remove();
    this.nextArrow?.remove();
  }

  ngAfterViewInit() {
    this.createArrows();

    // Arrows only toggle classes, so keep the scroll stream out of Angular's change detection.
    this.zone.runOutsideAngular(() => {
      this.unlisteners.push(this.renderer.listen(this.el.nativeElement, 'scroll', () => this.updateArrows()));
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.el.nativeElement);
    });
  }

  isScrollableHorizontally(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  private createArrows(): void {
    const hostElement: HTMLElement = this.el.nativeElement;
    const parent: HTMLElement = this.renderer.parentNode(hostElement);

    parent.querySelectorAll('.horizontal-scroll-arrow').forEach((arrow) => arrow.remove());

    this.prevArrow = this.buildArrow(CHEVRON_LEFT_SVG, 'before', -1);
    this.nextArrow = this.buildArrow(CHEVRON_RIGHT_SVG, 'after', 1);

    parent.insertBefore(this.prevArrow, parent.firstChild);
    parent.appendChild(this.nextArrow);

    this.updateArrows();
  }

  private buildArrow(icon: string, positionClass: string, direction: 1 | -1): HTMLElement {
    const arrow: HTMLButtonElement = this.renderer.createElement('button');
    arrow.type = 'button';
    arrow.classList.add('horizontal-scroll-arrow', positionClass, 'hidden');
    arrow.innerHTML = icon;
    this.unlisteners.push(
      this.renderer.listen(arrow, 'click', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.scrollByPage(direction);
      })
    );
    return arrow;
  }

  private scrollByPage(direction: 1 | -1): void {
    const hostElement: HTMLElement = this.el.nativeElement;
    hostElement.scrollBy({ left: direction * hostElement.clientWidth * 0.8, behavior: 'smooth' });
  }

  private updateArrows(): void {
    const hostElement: HTMLElement = this.el.nativeElement;
    const scrollable = this.isScrollableHorizontally(hostElement);
    const atStart = hostElement.scrollLeft <= EDGE_TOLERANCE;
    const atEnd = hostElement.scrollLeft + hostElement.clientWidth >= hostElement.scrollWidth - EDGE_TOLERANCE;

    this.prevArrow?.classList.toggle('hidden', !scrollable || atStart);
    this.nextArrow?.classList.toggle('hidden', !scrollable || atEnd);
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent): void {
    this.isDown = true;
    this.startX = event.pageX - this.el.nativeElement.offsetLeft;
    this.scrollLeft = this.el.nativeElement.scrollLeft;
    this.el.nativeElement.style.cursor = 'grabbing';
  }

  @HostListener('mouseleave')
  @HostListener('mouseup')
  onMouseUp(): void {
    this.isDown = false;
    this.el.nativeElement.style.cursor = 'grab';
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDown) return;
    event.preventDefault();
    const x = event.pageX - this.el.nativeElement.offsetLeft;
    const walk = (x - this.startX) * 1.5;
    this.el.nativeElement.scrollLeft = this.scrollLeft - walk;
  }
}
